import {
  SelectChipsBase,
  type SelectChipsBaseProps,
  type ChipOptionBase,
} from "@core/ui";
import { twMerge } from "tailwind-merge";
import { SelectableChip } from "./SelectableChip";

export type ChipOption = ChipOptionBase;

// Omit renderChip since we provide our own styled implementation
export type Props = Omit<SelectChipsBaseProps, "renderChip">;

/**
 * Styled select chips component.
 * Pure UI component - works with or without react-hook-form.
 *
 * @example
 * // Multi-select
 * <SelectChips
 *   name="tags"
 *   options={[
 *     { value: "react", label: "React" },
 *     { value: "vue", label: "Vue" },
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 *   maxItems={3}
 * />
 */
export function SelectChips({
  name,
  options,
  value = [],
  onChange,
  onBlur,
  error,
  disabled = false,
  maxItems = Infinity,
  id,
  className,
  ...props
}: Props) {
  const isMultiSelect = maxItems > 1;

  return (
    <SelectChipsBase
      name={name}
      options={options}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      disabled={disabled}
      maxItems={maxItems}
      id={id}
      className={twMerge(
        "flex flex-row flex-wrap gap-x-2 gap-y-3 p-1 items-center justify-start",
        className
      )}
      renderChip={({ option, isChecked, isDisabled, onChange: onChipChange }) => (
        <SelectableChip
          key={option.value}
          title={option.label}
          isChecked={isChecked}
          isDisabled={isDisabled}
          onChange={onChipChange}
          isSelectable
          isRemovable
          isCheckOnClick={!isMultiSelect}
        />
      )}
      {...props}
    />
  );
}

export default SelectChips;

